import { useEffect, useReducer, useState } from 'react'
import axios from '@fs/zion-axios'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlag } from '@fs/zion-flags'
import getPublicCollections from './authServices'
import { getGedcomXData } from '../recordHooks'
import { getCollectionDetails } from '../gedx/recordService'

const debug = zionDebug('zion-gedcomx:authHooks')

const collectionIdReducer = (state, action) => {
  switch (action.type) {
    case 'setCollectionId':
      return { collectionId: action.collectionId }
    case 'error':
      debug(`error getting collection id:`, action.response)
      return { error: action.response }
    default:
      return { ...state }
  }
}

/**
 * Hook to retrieve collection id for a persona ark
 * @param {String} path - persona, record, image ARK, search results URL
 * @param {String} queryString - query string on the ark's full URL
 * @param {Object} [matchParams] - an object of path params or null if we aren't checking discovery routes
 * @returns {{collectionId: string|undefined, error: *}} object with collection ID or error
 */
export function useCollectionId(path, queryString = '', matchParams) {
  const [state, dispatch] = useReducer(collectionIdReducer, {})
  const recordsImprovementIndexing = useFeatureFlag('rip_search_imageIndexing')
  const fromSLS = recordsImprovementIndexing.isOn

  useEffect(() => {
    let cancelled = false
    if (path?.indexOf('ark') > -1 || path?.indexOf('image/index') > -1) {
      if (path.indexOf('61903/3:') > -1 || path?.indexOf('image/index') > -1) {
        // handle 3:1 image arks and image viewers arks that have the owc param
        // owc param example: https://www.familysearch.org/search/image/index?owc=https://www.familysearch.org/platform/records/collections/1609792/waypoints
        const match = queryString.match(/cc=(\d+)/) || queryString.match(/\/collections\/(\d+)\//)

        const collectionId = match?.[1]
        if (collectionId) {
          debug(`collection id for ${path}:`, collectionId)
          dispatch({ type: 'setCollectionId', collectionId })
        } else if (!collectionId && path.indexOf('61903/3:') > -1) {
          // If the ark path we are testing doesn't have a 'cc' param, attempt to find the
          // collection ID by parsing through the image data ourselves
          getGedcomXData(path)
            .then((imageData) => {
              if (!cancelled) {
                debug(`image data for ${path}:`, imageData)
                const { collectionId: imageCollectionId } = getCollectionDetails(imageData.data)
                if (imageCollectionId) {
                  debug(`collection id for ${path}:`, imageCollectionId)
                  dispatch({ type: 'setCollectionId', collectionId: imageCollectionId })
                } else {
                  // We couldn't verify the collection id for this image, so we default to assuming this is a non-public image
                  dispatch({ type: 'error', response: 'Cannot get collection id from path' })
                }
              }
            })
            .catch(sendError)
        } else {
          // We couldn't verify the collection id for this image, so we default to assuming this is a non-public image
          dispatch({ type: 'error', response: 'Cannot get collection id from path' })
        }
      } else {
        // handle 1:1 record arks
        getGedcomXData(path, fromSLS)
          .then((recordData) => {
            if (!cancelled) {
              debug(`record data for ${path}:`, recordData)
              const { collectionId } = getCollectionDetails(recordData.data)
              dispatch({ type: 'setCollectionId', collectionId })
            }
          })
          .catch(sendError)
      }
    } else if (path?.indexOf('/results') > -1) {
      // handle search results with possible f.collectionId=1234 query string parameters
      const matches = queryString.split(/[&?]/).filter((item) => item.match(/^f\.collectionId=\d+$/i))
      if (matches.length === 1) {
        const match = matches[0].match(/f\.collectionId=(\d+)/i)
        const collectionId = match[1]
        debug(`collection id for ${path}:`, collectionId)
        dispatch({ type: 'setCollectionId', collectionId })
      } else {
        // dispatch an error because we will only return a single collection id and the user is searching multiples
        // the consumer will treat this situation as a "private" collection (require login when not in a discovery center)
        dispatch({ type: 'error', response: 'There is not one single collection id' })
      }
    } else if (matchParams && path?.includes('/discovery')) {
      if (matchParams?.collectionId) {
        const collectionId = matchParams.collectionId
        debug(`collection id for ${path}:`, collectionId)
        dispatch({ type: 'setCollectionId', collectionId })
      } else {
        // the consuming app has wired up this discovery route to a path type we do not support
        dispatch({ type: 'error', response: 'Cannot get collection id from path' })
      }
    } else {
      // the consuming app has wired up this route to a path type we do not support
      dispatch({ type: 'error', response: 'Cannot get collection id from path' })
    }

    return () => {
      cancelled = true
    }

    function sendError(response) {
      if (!cancelled) {
        debug(`error getting some data for ark ${path}:`, response)
        dispatch({ type: 'error', response })
      }
    }
  }, [fromSLS, path, queryString, matchParams])

  return state
}

/**
 * Hook to decide if an ark is in a public collection. If an image ark is provided, it has to have the "cc=" parameter to work properly.
 * @param {String} path - the record, persona, or image ark; or search results path
 * @param {String} queryString - the query string on the record ark's full URL
 * @param {Object|null} matchParams - an object of the params in the path from the router. Or is null if we are not checking discovery routes
 * @returns {{publicLoading: boolean, inPublicCollection: boolean}}
 */
export default function useInPublicCollection(path, queryString, matchParams) {
  const collectionState = useCollectionId(path, queryString, matchParams)
  const [publicCollections, setPublicCollections] = useState(null)
  const [isPublic, setPublic] = useState({ publicLoading: true, inPublicCollection: false })

  useEffect(() => {
    let cancelled = false
    const cancelSource = axios.CancelToken.source()
    getPublicCollections({ cancelSource })
      .then((response) => {
        if (!cancelled) {
          debug(`public collections response:`, response)
          setPublicCollections(Object.keys(response.data))
        }
      })
      .catch((error) => {
        // swallow errors with a non-error code; the user may have navigated away or been redirected to login
        if (!cancelled && (error?.status >= 400 || error?.response?.status >= 400)) {
          console.error(`error retrieving public collections`, error)
        }
      })
    return () => {
      cancelled = true
      cancelSource.cancel()
    }
  }, [])

  useEffect(() => {
    if (publicCollections?.length && collectionState.collectionId) {
      debug(
        'collection id:',
        collectionState.collectionId,
        'is public?',
        publicCollections.indexOf(collectionState.collectionId) > -1
      )
      setPublic({
        inPublicCollection: publicCollections.indexOf(collectionState.collectionId) > -1,
        publicLoading: false,
      })
    } else {
      if (collectionState.error) {
        debug(`error getting collection id:`, collectionState.error)
      }
      debug(`not a public collection or no collection id in state`, {
        publicCollections,
        collectionId: collectionState?.collectionId,
      })

      // if we can't get the collection, assume we're not in a public collection
      setPublic({
        inPublicCollection: false,
        publicLoading: false,
      })
    }
  }, [collectionState, publicCollections])
  debug(`in public collection?:`, isPublic, 'collection id?', collectionState.collectionId)
  return isPublic
}
